/* global PowerPoint */
import * as React from "react";
import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { Spinner, SidebarProvider, SidebarInset } from "@finster/ui";
import { ChevronDown, Loader2, Square, ArrowUpRight } from "lucide-react";
import Login from "../../shared/components/Login";
import { buildAccountScopeKey } from "../../shared/auth/accountScope";
import { getUserTasks, FinsterTask } from "../finsterApi";
import { TasksHome } from "./TasksHome";
import { PdfUploader } from "./PdfUploader";
import { DeckCreator, type CreationProgress, type DeckConfig } from "./DeckCreator/DeckCreator";
import {
  freezeApprovedDeckPlanContext,
  getDeckPlanTransientContext,
  reattachDeckPlanTransientContext,
  stripDeckPlanTransientContext,
  type DeckPlan,
  type DeckPlanTransientContext,
  type ReviewSkillBindingState,
} from "../services/ai/deckPlannerService";
import {
  createReviewOperationId,
  type PlanningEvidencePack,
} from "../services/ai/planningEvidence";
import type { SkillRunContext } from "../services/ai/skills";
import { ExtractedImage } from "../services/pdfService";
import {
  openDeckCreatorDialog,
  isDeckCreatorDialogOpen,
  isDeckCreatorReviewSessionOpen,
  pushPlanToDeckCreatorDialog,
  resolveDeckCreatorOpenPlan,
  getRememberedDeckCreatorPlan,
  cancelDeckCreatorDialog,
} from "../services/deckCreatorDialogService";
import {
  SLIDE_LIBRARY_COMMAND_MESSAGE_KIND,
  SLIDE_LIBRARY_RESPONSE_MESSAGE_KIND,
  type SlideLibraryCommandEnvelope,
  type SlideLibraryResponseEnvelope,
} from "../slideLibrary/ui/dialog/dialogTransport";
import { createBlankSlideAtEnd } from "../pptxSlideUtils";
import { goToSlideById } from "../services/slideService";
import { getBrand, type BrandId } from "../services/ai/brandRegistry";
import {
  useAuth,
  useTabs,
  useQueryHandler,
  useDeckCreation,
} from "../hooks";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useArmedDefaultsBrand } from "../hooks/useArmedDefaultsBrand";
import { useDeckReview } from "../hooks/useDeckReview";
import DeckReview from "./DeckReview";
import { ChatView } from "./ChatView";
import type { ChatTaskContext } from "../types";
import { AllChatsView } from "./AllChatsView";
import { SlideCitationsPanel } from "./SlideCitationsPanel";
import Onboarding from "./Onboarding";
import {
  hasCompletedOnboarding,
  markOnboardingCompleted,
} from "../../shared/services/onboardingService";
import { shouldAttemptFactSetAutoLogin } from "../services/authService";
import {
  activatePresentationViaIntents,
  getActivePresentationRefViaIntents,
  type FactSetPresentationRef,
} from "../services/factset/intentBridgeService";
import { shouldUseFactSetBridge } from "../services/presentationHost";
import { isPowerPointPreslinkSlideLibraryEnabled } from "../services/featureFlagService";
import {
  insertSlideLibraryItemsViaFactSet,
  type SlideLibraryInsertionResult,
} from "../slideLibrary/ui/host/insertionService";
import { insertPreslinkCollectionSlides } from "../slideLibrary/ui/host/preslinkInsertionService";
import { buildSkillsManagementUrl } from "../../shared/skills";
import { useMatchingSkillBanner } from "../../shared/skills/hooks/useMatchingSkillBanner";
import { getClientBuildVersion } from "../../shared/clientUpdate/buildVersion";
import { fetchCurrentVersion } from "../../shared/clientUpdate/fetchVersion";
import {
  createClientUpdateController,
  type ClientUpdateController,
} from "../../shared/clientUpdate/updateController";
import { subscribeToUpgradeRequired } from "../../shared/clientUpdate/upgradeRequired";
import { isPowerPointClientUpdateBusy } from "../clientUpdateBusy";
import { hasAnyInFlightPowerPointWork } from "../services/inFlightTabRegistry";
import { saveTabsState } from "../services/storageService";

const PENDING_UPDATE_IDLE_RECHECK_MS = 1000;

function BackgroundDeckCreationBanner({
  items,
}: {
  items: {
    operationId: string;
    progress: CreationProgress;
    onCancel: () => void;
    onGoToSlide?: () => void;
  }[];
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex-none border-b border-border bg-background px-3 py-2">
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-sm">
        <button
          type="button"
          onClick={() => setExpanded((next) => !next)}
          className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left transition-colors hover:bg-accent/30"
        >
          <div className="flex items-center gap-2 text-foreground">
            <div className="flex h-5 w-5 items-center justify-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
            <span className="text-xs font-semibold">On-going actions</span>
          </div>
          <div className="text-muted-foreground">
            <ChevronDown
              className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : "rotate-0"}`}
            />
          </div>
        </button>

        {expanded && (
          <div className="border-t border-border px-4 py-3">
            <div className="space-y-2">
              {items.map(({ operationId, progress, onCancel, onGoToSlide }) => {
                const currentSlide = Math.min(progress.currentSlide + 1, progress.totalSlides);
                const isFinalReview = progress.phase === "finalReview";
                const isPolishing = progress.phase === "polishing";
                const finalReviewTotal = progress.finalReview?.total ?? progress.totalSlides;
                const finalReviewCurrent = Math.min(
                  progress.finalReview?.current ?? currentSlide,
                  finalReviewTotal
                );
                const polishTotal = progress.polish?.total ?? progress.totalSlides;
                const polishCurrent = Math.min(progress.polish?.current ?? 0, polishTotal);
                const percent =
                  isPolishing && polishTotal > 0
                    ? Math.min(100, Math.round((polishCurrent / polishTotal) * 100))
                    : isFinalReview && finalReviewTotal > 0
                      ? Math.min(100, Math.round((finalReviewCurrent / finalReviewTotal) * 100))
                      : progress.totalSlides > 0
                        ? Math.min(100, Math.round((currentSlide / progress.totalSlides) * 100))
                        : 0;
                const targetName = progress.targetPresentationName?.trim();
                const slideLabel = isPolishing
                  ? `Polishing ${polishCurrent} of ${polishTotal}`
                  : isFinalReview
                    ? `Final review ${finalReviewCurrent} of ${finalReviewTotal}`
                    : `Slide ${currentSlide} of ${progress.totalSlides}`;
                const compactTitle = isPolishing
                  ? `Polishing slides (${polishCurrent}/${polishTotal})`
                  : isFinalReview
                    ? `Final review (${finalReviewCurrent}/${finalReviewTotal})`
                    : `Building your slides (${currentSlide}/${progress.totalSlides})`;
                const presentationLabel = targetName || "target presentation";
                return (
                  // Item wrapper is a `<div>` (not `<button>`) so the
                  // Stop button can live INSIDE without invalid nested
                  // buttons. The whole tile is still clickable to open
                  // the generating slide via the onClick handler;
                  // Stop's `e.stopPropagation()` keeps the two
                  // affordances independent.
                  <div
                    key={operationId}
                    role="button"
                    tabIndex={0}
                    onClick={() => onGoToSlide?.()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onGoToSlide?.();
                      }
                    }}
                    className="w-full cursor-pointer rounded-md px-1 py-0.5 text-left transition-colors hover:bg-accent/30"
                    title="Open current generating slide"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-foreground">
                          {compactTitle}
                        </div>
                        <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                          <span className="font-semibold text-foreground">{presentationLabel}</span>{" "}
                          in progress
                        </div>
                      </div>
                      <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>
                    <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-border">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                      <span className="min-w-0 flex-1 truncate">
                        {slideLabel}{" "}
                        <span className="font-semibold text-primary tabular-nums">
                          · {percent}%
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancel();
                        }}
                        className="flex h-7 shrink-0 cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Square className="h-3 w-3" />
                        Stop
                      </button>
                    </div>
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="text-xs text-muted-foreground">No ongoing actions</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const App: React.FC = () => {
  const [sideNavCollapsed, setSideNavCollapsed] = useState(true);
  const log = useCallback((message: string) => {
    console.log(message);
  }, []);
  const { authState, authChecked, handleLogin } = useAuth({
    log,
  });
  const activeBrand = useActiveBrand(authState.user?.companyId);
  const storageUserId = authState.user?.id || authState.user?.email || null;
  const accountScopeKey = authState.isAuthenticated
    ? buildAccountScopeKey({
        userId: authState.user?.id,
        userEmail: authState.user?.email,
        companyId: authState.user?.companyId,
      })
    : null;
  const accountScopeKeyRef = useRef(accountScopeKey);
  accountScopeKeyRef.current = accountScopeKey;
  const clientUpdateControllerRef = useRef<ClientUpdateController | null>(null);
  const [clientUpdatePending, setClientUpdatePending] = useState(false);
  const manageSkillsUrl = buildSkillsManagementUrl(authState.features?.finsterWebUrl);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const shouldShowOnboarding = Boolean(
    authState.isAuthenticated &&
    storageUserId &&
    !onboardingDismissed &&
    !hasCompletedOnboarding("powerpoint", storageUserId)
  );
  // User tasks from Finster API
  const [userTasks, setUserTasks] = useState<FinsterTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState<boolean>(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const userTasksOwnerRef = useRef(accountScopeKey);
  const userTasksAreCurrent = userTasksOwnerRef.current === accountScopeKey;
  const effectiveUserTasks = userTasksAreCurrent ? userTasks : [];
  const effectiveTasksLoading = userTasksAreCurrent ? tasksLoading : false;
  const effectiveTasksError = userTasksAreCurrent ? tasksError : null;

  // View state: Skills overlays chat so the unsent composer remains mounted.
  const [currentView, setCurrentView] = useState<
    "home" | "chat" | "pdf" | "allChats" | "citations" | "skills"
  >("chat");
  const [allChatsSearchFocusKey, setAllChatsSearchFocusKey] = useState(0);
  const [skillsComposerFocusKey, setSkillsComposerFocusKey] = useState(0);
  const [skillsContextVersion, setSkillsContextVersion] = useState(0);

  // PDF extraction state (lifted for persistence across tab switches)
  const [pdfExtractedImages, setPdfExtractedImages] = useState<ExtractedImage[]>([]);
  const [pdfSelectedImages, setPdfSelectedImages] = useState<Set<number>>(new Set());
  const pdfStateOwnerRef = useRef(accountScopeKey);
  const pdfStateIsCurrent = pdfStateOwnerRef.current === accountScopeKey;
  const effectivePdfExtractedImages = pdfStateIsCurrent ? pdfExtractedImages : [];
  const effectivePdfSelectedImages = pdfStateIsCurrent ? pdfSelectedImages : new Set<number>();
  const setScopedPdfExtractedImages: React.Dispatch<React.SetStateAction<ExtractedImage[]>> = (
    value
  ) => {
    if (accountScopeKeyRef.current !== accountScopeKey) return;
    pdfStateOwnerRef.current = accountScopeKey;
    setPdfExtractedImages(value);
  };
  const setScopedPdfSelectedImages: React.Dispatch<React.SetStateAction<Set<number>>> = (value) => {
    if (accountScopeKeyRef.current !== accountScopeKey) return;
    pdfStateOwnerRef.current = accountScopeKey;
    setPdfSelectedImages(value);
  };
  const logPdfActivity = (message: string) => {
    if (accountScopeKeyRef.current === accountScopeKey) {
      log(message);
    }
  };

  // AI view mode: default to chat; enter create/review from sidebar actions.
  const [aiMode, setAiMode] = useState<"chat" | "create" | "review">("chat");
  const isCreatingDeckRef = useRef(false);

  // Deck creator config and plan from dialog
  const [pendingDeckConfig, setPendingDeckConfig] = useState<DeckConfig | null>(null);
  const [pendingDeckPlan, setPendingDeckPlan] = useState<DeckPlan | null>(null);
  const pendingDeckPlanRef = useRef<DeckPlan | null>(null);
  pendingDeckPlanRef.current = pendingDeckPlan;
  const activeReviewContextRef = useRef<DeckPlanTransientContext | null>(null);
  const activeReviewOperationIdRef = useRef<string | null>(null);
  const activeReviewOperationGenerationRef = useRef(0);
  const reviewOperationActivitiesRef = useRef<
    Map<
      string,
      {
        generation: number;
        wait: Promise<void>;
        supersede: () => void;
      }
    >
  >(new Map());
  const [pendingDeckPresentationRef, setPendingDeckPresentationRef] =
    useState<FactSetPresentationRef | null>(null);
  const reviewOpenWaitRef = useRef<Promise<void> | null>(null);
  const cancelReviewOpenWaitRef = useRef<(() => void) | null>(null);
  const reviewSessionGenRef = useRef(0);
  const appMountedRef = useRef(true);
  const deckCreationTargetPresentationIdsRef = useRef<string[]>([]);
  const brandStyleRef = useRef<BrandId | null>(null);
  const plannedBrandStyleRef = useRef<BrandId | null>(null);
  const plannedBrandStyleOperationIdRef = useRef<string | null>(null);
  const capturePlannedBrandStyle = (
    reviewOperationId: string,
    brandId?: BrandId | null
  ): void => {
    if (!reviewOperationId || plannedBrandStyleOperationIdRef.current === reviewOperationId) {
      return;
    }
    plannedBrandStyleRef.current = brandId !== undefined ? brandId : brandStyleRef.current;
    plannedBrandStyleOperationIdRef.current = reviewOperationId;
  };
  const clearPlannedBrandStyle = (): void => {
    plannedBrandStyleRef.current = null;
    plannedBrandStyleOperationIdRef.current = null;
  };

  const clearPrivateOperationBindings = useCallback(() => {
    for (const activity of reviewOperationActivitiesRef.current.values()) {
      activity.supersede();
    }
    activeReviewContextRef.current = null;
    activeReviewOperationIdRef.current = null;
    activeReviewOperationGenerationRef.current = 0;
    reviewOperationActivitiesRef.current.clear();
  }, []);

  const invalidatePrivateReviewSession = useCallback(() => {
    const hasOwnedReview = Boolean(
      reviewOpenWaitRef.current ||
      pendingDeckPlanRef.current ||
      activeReviewContextRef.current ||
      activeReviewOperationIdRef.current ||
      reviewOperationActivitiesRef.current.size > 0 ||
      isDeckCreatorReviewSessionOpen()
    );
    if (!hasOwnedReview) return;

    const settleOpenWait = cancelReviewOpenWaitRef.current;
    reviewSessionGenRef.current += 1;
    reviewOpenWaitRef.current = null;
    cancelReviewOpenWaitRef.current = null;
    pendingDeckPlanRef.current = null;
    clearPlannedBrandStyle();
    clearPrivateOperationBindings();
    settleOpenWait?.();
    cancelDeckCreatorDialog();
  }, [clearPrivateOperationBindings]);

  useLayoutEffect(() => {
    appMountedRef.current = true;
    return () => {
      appMountedRef.current = false;
      invalidatePrivateReviewSession();
    };
  }, [invalidatePrivateReviewSession]);

  const releaseActiveReviewBinding = useCallback(() => {
    const reviewOperationId =
      activeReviewContextRef.current?.reviewOperationId ?? activeReviewOperationIdRef.current;
    if (reviewOperationId) {
      reviewOperationActivitiesRef.current.get(reviewOperationId)?.supersede();
      reviewOperationActivitiesRef.current.delete(reviewOperationId);
    }
    activeReviewContextRef.current = null;
    activeReviewOperationIdRef.current = null;
    activeReviewOperationGenerationRef.current = 0;
  }, []);

  const registerReviewOperationActivity = useCallback(
    (
      reviewOperationId: string,
      reviewOperationGeneration: number,
      activity: Promise<void>
    ): boolean => {
      const currentPlan = pendingDeckPlanRef.current;
      const currentContext = activeReviewContextRef.current;
      const activeOperationId =
        currentContext?.reviewOperationId ?? activeReviewOperationIdRef.current;
      const activeGeneration =
        currentContext?.reviewOperationGeneration ?? activeReviewOperationGenerationRef.current;
      const reviewIsActive =
        Boolean(currentPlan) &&
        (isDeckCreatorReviewSessionOpen() || Boolean(reviewOpenWaitRef.current));
      if (
        (!reviewIsActive && (currentPlan || activeOperationId)) ||
        (activeOperationId && activeOperationId !== reviewOperationId)
      ) {
        return false;
      }
      if (activeOperationId === reviewOperationId && reviewOperationGeneration < activeGeneration) {
        return false;
      }

      let nextContext = currentContext;
      if (currentContext?.reviewOperationId === reviewOperationId) {
        nextContext = Object.freeze({
          ...currentContext,
          reviewOperationGeneration,
        });
      } else if (currentPlan) {
        const planContext = getDeckPlanTransientContext(currentPlan);
        nextContext =
          planContext?.reviewOperationId === reviewOperationId
            ? Object.freeze({
                ...planContext,
                reviewOperationGeneration,
              })
            : Object.freeze({
                reviewOperationId,
                reviewOperationGeneration,
                reviewSkillBindingState: "unbound" as const,
              });
      }

      if (nextContext) {
        activeReviewContextRef.current = nextContext;
        const reboundPlan = currentPlan
          ? reattachDeckPlanTransientContext(
              stripDeckPlanTransientContext(currentPlan),
              nextContext
            )
          : null;
        if (reboundPlan) {
          pendingDeckPlanRef.current = reboundPlan;
          setPendingDeckPlan(reboundPlan);
        }
      }
      activeReviewOperationIdRef.current = reviewOperationId;
      activeReviewOperationGenerationRef.current = reviewOperationGeneration;

      let supersede: () => void = () => {};
      const superseded = new Promise<void>((resolve) => {
        supersede = resolve;
      });
      const wait = Promise.race([
        activity.then(
          () => undefined,
          () => undefined
        ),
        superseded,
      ]);
      const previous = reviewOperationActivitiesRef.current.get(reviewOperationId);
      previous?.supersede();
      const registered = {
        generation: reviewOperationGeneration,
        wait,
        supersede,
      };
      reviewOperationActivitiesRef.current.set(reviewOperationId, registered);
      void wait.then(() => {
        if (reviewOperationActivitiesRef.current.get(reviewOperationId) === registered) {
          reviewOperationActivitiesRef.current.delete(reviewOperationId);
        }
        if (
          !activeReviewContextRef.current &&
          !pendingDeckPlanRef.current &&
          activeReviewOperationIdRef.current === reviewOperationId &&
          activeReviewOperationGenerationRef.current === reviewOperationGeneration
        ) {
          activeReviewOperationIdRef.current = null;
          activeReviewOperationGenerationRef.current = 0;
        }
      });
      return true;
    },
    []
  );

  const waitForReviewOperationIdle = useCallback(
    async (reviewOperationId: string, reviewOperationGeneration: number): Promise<void> => {
      for (;;) {
        const activity = reviewOperationActivitiesRef.current.get(reviewOperationId);
        if (!activity || activity.generation !== reviewOperationGeneration) return;
        await activity.wait;
        if (reviewOperationActivitiesRef.current.get(reviewOperationId) === activity) {
          reviewOperationActivitiesRef.current.delete(reviewOperationId);
          return;
        }
      }
    },
    []
  );

  const waitForActiveReviewOperationIdle = useCallback(async (): Promise<void> => {
    for (;;) {
      const activeContext = activeReviewContextRef.current;
      const reviewOperationId =
        activeContext?.reviewOperationId ?? activeReviewOperationIdRef.current;
      if (!reviewOperationId) return;
      const reviewOperationGeneration =
        activeContext?.reviewOperationGeneration ?? activeReviewOperationGenerationRef.current;
      await waitForReviewOperationIdle(reviewOperationId, reviewOperationGeneration);
      const currentContext = activeReviewContextRef.current;
      const currentReviewOperationId =
        currentContext?.reviewOperationId ?? activeReviewOperationIdRef.current;
      const currentReviewOperationGeneration =
        currentContext?.reviewOperationGeneration ?? activeReviewOperationGenerationRef.current;
      if (
        !currentReviewOperationId ||
        (currentReviewOperationId === reviewOperationId &&
          currentReviewOperationGeneration === reviewOperationGeneration)
      ) {
        return;
      }
    }
  }, [waitForReviewOperationIdle]);

  const syncDeckCreationModeWithActivePresentation = useCallback(() => {
    if (!isCreatingDeckRef.current) {
      setAiMode("chat");
      return;
    }

    if (!shouldUseFactSetBridge()) {
      return;
    }

    const targetPresentationIds = deckCreationTargetPresentationIdsRef.current;
    const pendingTargetId = pendingDeckPresentationRef?.openPresentationId?.trim() || "";
    if (targetPresentationIds.length === 0 && !pendingTargetId) {
      setAiMode("create");
      return;
    }

    void getActivePresentationRefViaIntents(log)
      .then((activeRef) => {
        // The IPC round-trip is async — by the time it resolves the
        // deck operation may have completed and `isCreatingDeck` may
        // already be false. Acting on a stale activeRef in that case
        // would force `setAiMode("create")` (or "chat") with no
        // subsequent effect-fire to correct it (the deps that drive
        // the calling effect won't change a second time), stranding
        // the user in the wrong view. Re-check the live ref.
        if (!isCreatingDeckRef.current) return;
        const activePresentationId = activeRef?.openPresentationId?.trim() || "";
        const isTargetPresentation =
          Boolean(activePresentationId) &&
          (targetPresentationIds.includes(activePresentationId) ||
            activePresentationId === pendingTargetId);
        setAiMode(isTargetPresentation ? "create" : "chat");
      })
      .catch(() => {
        // Same guard as the `.then()`: if creation finished while the
        // IPC was in flight, do NOT force "create" mode on a transient
        // intent-bridge failure — the user has moved on. Only restore
        // "create" while creation is genuinely still active, as a safe
        // fallback when we couldn't read the active presentation.
        if (!isCreatingDeckRef.current) return;
        setAiMode("create");
      });
  }, [log, pendingDeckPresentationRef]);

  const onDocumentChange = useCallback(() => {
    reviewSessionGenRef.current += 1;
    clearPrivateOperationBindings();
    pendingDeckPlanRef.current = null;
    setPendingDeckPlan(null);
    setPendingDeckConfig(null);
    setPendingDeckPresentationRef(null);
    setPdfExtractedImages([]);
    setPdfSelectedImages(new Set());
    setCurrentView("chat");
    setSkillsContextVersion((version) => version + 1);
    syncDeckCreationModeWithActivePresentation();
  }, [clearPrivateOperationBindings, syncDeckCreationModeWithActivePresentation]);

  const deckReviewRef = useRef<ReturnType<typeof useDeckReview> | null>(null);

  const onSlideChange = useCallback(() => {
    setCurrentView("chat");
    setSkillsContextVersion((version) => version + 1);
    syncDeckCreationModeWithActivePresentation();
  }, [syncDeckCreationModeWithActivePresentation]);

  const {
    tabsState,
    setTabsState,
    activeTab,
    sortedTabs,
    deckHasSlides,
    syncTabsWithPresentation,
    updateActiveTab,
    handleTabClick,
    handleTabClose,
    handleAddTab,
    handleClearHistory,
  } = useTabs({
    isAuthenticated: authState.isAuthenticated,
    storageUserId: accountScopeKey,
    onDocumentChange,
    onSlideChange,
  });
  const tabsStateRef = useRef(tabsState);
  tabsStateRef.current = tabsState;

  const operationOwnerRef = useRef(accountScopeKey);
  const privateOperationStateIsCurrent = operationOwnerRef.current === accountScopeKey;
  useLayoutEffect(() => {
    const effectAccountScopeKey = accountScopeKey;
    const ownerChanged = operationOwnerRef.current !== accountScopeKey;
    operationOwnerRef.current = accountScopeKey;
    // Rollout changes affect new submissions only. An already-bound Review or
    // build keeps its submit-time snapshot; auth/account changes still clear it.
    if (!authState.isAuthenticated || ownerChanged) {
      invalidatePrivateReviewSession();
      pendingDeckPlanRef.current = null;
      setPendingDeckPlan(null);
      setPendingDeckConfig(null);
      setPendingDeckPresentationRef(null);
    }

    return () => {
      if (appMountedRef.current && accountScopeKeyRef.current !== effectAccountScopeKey) {
        invalidatePrivateReviewSession();
      }
    };
  }, [accountScopeKey, authState.isAuthenticated, invalidatePrivateReviewSession]);

  const handleOpenSkills = useCallback(() => {
    setAiMode("chat");
    setCurrentView("skills");
  }, []);

  const handleCloseSkills = useCallback(() => {
    setCurrentView("chat");
    setAiMode("chat");
    setSkillsComposerFocusKey((key) => key + 1);
  }, []);

  const handleSelectTabFromNav = useCallback(
    (tabId: string) => {
      setCurrentView("chat");
      setAiMode("chat");
      setSkillsContextVersion((version) => version + 1);
      handleTabClick(tabId);
    },
    [handleTabClick]
  );

  const postSlideLibraryResponse = useCallback(
    (
      source: MessageEventSource | null,
      origin: string,
      payload: SlideLibraryResponseEnvelope["payload"]
    ) => {
      if (!source || !("postMessage" in source)) return;
      const envelope: SlideLibraryResponseEnvelope = {
        kind: SLIDE_LIBRARY_RESPONSE_MESSAGE_KIND,
        payload,
      };
      (source as Window).postMessage(envelope, origin);
    },
    []
  );

  const slideLibraryInsertInFlightRef = useRef(false);

  const handleSlideLibraryCommand = useCallback(
    async (
      command: SlideLibraryCommandEnvelope["payload"],
      source: MessageEventSource | null,
      origin: string
    ) => {
      if (command.type !== "addToPresentation") return;

      const respond = (
        payload: Omit<SlideLibraryResponseEnvelope["payload"], "requestId" | "type">
      ) =>
        postSlideLibraryResponse(source, origin, {
          type: "addToPresentationResult",
          requestId: command.requestId,
          ...payload,
        });

      if (!shouldUseFactSetBridge()) {
        respond({
          insertedCount: 0,
          failedCount: command.items.length,
          error: "Add to presentation is only available in FactSet COM mode.",
        });
        return;
      }

      if (command.items.length === 0) {
        respond({
          insertedCount: 0,
          failedCount: 0,
          error: "No slides were selected.",
        });
        return;
      }

      if (command.preslinkContext && !isPowerPointPreslinkSlideLibraryEnabled()) {
        respond({
          insertedCount: 0,
          failedCount: command.items.length,
          error: "PresLink slide library insertion is unavailable.",
        });
        return;
      }

      if (slideLibraryInsertInFlightRef.current) {
        respond({
          insertedCount: 0,
          failedCount: command.items.length,
          error: "Another slide insert is still running. Wait for it to finish, then try again.",
        });
        return;
      }

      slideLibraryInsertInFlightRef.current = true;
      try {
        let insertionResult: SlideLibraryInsertionResult;
        if (command.preslinkContext) {
          insertionResult = await insertPreslinkCollectionSlides({
            collectionId: command.preslinkContext.collectionId,
            companyId: command.preslinkContext.companyId,
            items: command.items,
            log,
          });
        } else {
          let targetPresentationRef: FactSetPresentationRef | null = null;
          try {
            targetPresentationRef = await getActivePresentationRefViaIntents(log);
          } catch (error) {
            log(
              `Could not capture target presentation before slide-library insertion: ${
                error instanceof Error ? error.message : String(error)
              }`
            );
          }
          insertionResult = await insertSlideLibraryItemsViaFactSet({
            items: command.items,
            log,
            presentationRef: targetPresentationRef,
          });
        }

        if (insertionResult.insertedCount > 0) {
          await syncTabsWithPresentation();
        }

        const firstFailure = insertionResult.failures[0]?.error;
        respond({
          insertedCount: insertionResult.insertedCount,
          failedCount: insertionResult.failures.length,
          error:
            insertionResult.failures.length > 0
              ? firstFailure || "Some slides could not be inserted."
              : undefined,
        });
      } catch (error) {
        respond({
          insertedCount: 0,
          failedCount: command.items.length,
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        slideLibraryInsertInFlightRef.current = false;
      }
    },
    [log, postSlideLibraryResponse, syncTabsWithPresentation]
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as Partial<SlideLibraryCommandEnvelope> | null;
      if (
        data?.kind !== SLIDE_LIBRARY_COMMAND_MESSAGE_KIND ||
        data.payload?.type !== "addToPresentation"
      ) {
        return;
      }
      void handleSlideLibraryCommand(data.payload, event.source, event.origin);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleSlideLibraryCommand]);

  const handleSideNavAction = useCallback(
    (actionId: string) => {
      if (actionId === "proof-read") {
        setCurrentView("chat");
        setAiMode("review");
      } else if (actionId === "pdf-extractor") {
        setCurrentView("pdf");
      } else if (actionId === "citations") {
        setCurrentView("citations");
      } else if (actionId === "view-all-chats") {
        setCurrentView("allChats");
        setAiMode("chat");
      } else if (actionId === "search-chats") {
        setCurrentView("allChats");
        setAllChatsSearchFocusKey((key) => key + 1);
      } else if (actionId === "new-slide") {
        createBlankSlideAtEnd().then(async (slideId) => {
          if (!slideId) return;
          log(`Created new blank slide: ${slideId}`);
          await syncTabsWithPresentation();
          setTabsState((prev) => {
            const tab = prev.tabs.find((t) => t.session.slideId === slideId);
            if (tab) return { ...prev, activeTabId: tab.id };
            return prev;
          });
          setCurrentView("chat");
          setAiMode("chat");
          void goToSlideById(slideId);
        });
      }
    },
    []
  );

  // Task context from Finster - gets included in next AI query
  const [taskContext, setTaskContext] = useState<ChatTaskContext | null>(null);
  const taskContextOwnerRef = useRef(accountScopeKey);
  const effectiveTaskContext = taskContextOwnerRef.current === accountScopeKey ? taskContext : null;
  const updateTaskContext = (context: ChatTaskContext | null) => {
    taskContextOwnerRef.current = accountScopeKey;
    setTaskContext(context);
  };
  const taskContextRef = useRef(effectiveTaskContext);
  taskContextRef.current = effectiveTaskContext;
  const { brandId: brandStyle, setBrandId: setBrandStyle } = useArmedDefaultsBrand(
    authState.isAuthenticated ? (activeBrand?.id ?? getBrand("default")?.id ?? null) : null
  );
  brandStyleRef.current = brandStyle;
  const [plannerMode, setPlannerMode] = useState(false);

  const handlePresentDeckPlan = useCallback(
    (plan: DeckPlan, options?: { brandId?: BrandId | null }): Promise<void> => {
      const operationAccountScopeKey = accountScopeKey;
      const isAccountCurrent = (): boolean =>
        appMountedRef.current && accountScopeKeyRef.current === operationAccountScopeKey;
      if (!isAccountCurrent()) {
        return Promise.resolve();
      }
      const previousContext = activeReviewContextRef.current;
      let incomingContext = getDeckPlanTransientContext(plan) ?? null;
      if (!incomingContext) {
        incomingContext =
          previousContext ??
          Object.freeze({
            reviewOperationId: activeReviewOperationIdRef.current ?? createReviewOperationId(),
            reviewOperationGeneration: activeReviewOperationIdRef.current
              ? activeReviewOperationGenerationRef.current
              : 0,
            reviewSkillBindingState: "unbound" as const,
          });
      }
      const previousOperationId =
        previousContext?.reviewOperationId ?? activeReviewOperationIdRef.current;
      const previousGeneration =
        previousContext?.reviewOperationGeneration ?? activeReviewOperationGenerationRef.current;
      const incomingOperationId = incomingContext.reviewOperationId;
      if (
        previousOperationId === incomingOperationId &&
        incomingContext.reviewOperationGeneration < previousGeneration
      ) {
        return Promise.resolve();
      }
      if (previousOperationId && previousOperationId !== incomingOperationId) {
        reviewOperationActivitiesRef.current.get(previousOperationId)?.supersede();
        reviewOperationActivitiesRef.current.delete(previousOperationId);
      }
      capturePlannedBrandStyle(
        incomingOperationId,
        options && "brandId" in options ? options.brandId : undefined
      );
      activeReviewContextRef.current = incomingContext;
      activeReviewOperationIdRef.current = incomingOperationId;
      activeReviewOperationGenerationRef.current = incomingContext.reviewOperationGeneration;
      const privatePlan = reattachDeckPlanTransientContext(
        stripDeckPlanTransientContext(plan),
        incomingContext
      );
      const dialogPlan = stripDeckPlanTransientContext(privatePlan);
      pendingDeckPlanRef.current = privatePlan;
      setPendingDeckPlan(privatePlan);
      if (isDeckCreatorDialogOpen()) {
        if (!isAccountCurrent()) return Promise.resolve();
        pushPlanToDeckCreatorDialog(dialogPlan);
        if (!isAccountCurrent()) return Promise.resolve();
        log(`Updated Review plan: ${dialogPlan.topic} (${dialogPlan.slides.length} slides)`);
        return Promise.resolve();
      }

      const openingWait = reviewOpenWaitRef.current;
      if (openingWait) {
        const openingSessionGen = reviewSessionGenRef.current;
        // Queue the latest plan for the in-flight open. After that wait
        // settles, re-enter so a closed Review starts a new session instead
        // of treating the already-resolved open as success.
        pushPlanToDeckCreatorDialog(dialogPlan);
        return openingWait.then(() => {
          if (!isAccountCurrent() || openingSessionGen !== reviewSessionGenRef.current) {
            return;
          }
          if (reviewOpenWaitRef.current === openingWait) {
            reviewOpenWaitRef.current = null;
            cancelReviewOpenWaitRef.current = null;
          }
          // Review may already have closed; start a new session rather than
          // treating the resolved open-wait as success. The dialog service
          // must finish the prior session without invalidating the new
          // slide-preview stream.
          return handlePresentDeckPlan(privatePlan);
        });
      }

      pushPlanToDeckCreatorDialog(dialogPlan);
      const sessionGen = ++reviewSessionGenRef.current;
      let openedSettled = false;
      let resolveOpened: () => void = () => {};
      let rejectOpened: (error: Error) => void = () => {};
      const opened = new Promise<void>((resolve, reject) => {
        resolveOpened = resolve;
        rejectOpened = reject;
      });
      const isSessionCurrent = (): boolean =>
        isAccountCurrent() && sessionGen === reviewSessionGenRef.current;

      const releaseOpenWait = (): void => {
        if (reviewOpenWaitRef.current === opened) {
          reviewOpenWaitRef.current = null;
          cancelReviewOpenWaitRef.current = null;
        }
      };
      const markOpened = (): void => {
        if (openedSettled) return;
        openedSettled = true;
        // Release as soon as Review is showing (or the open failed). Do not
        // wait for the dialog result; otherwise a close leaves this wait set
        // while isDeckCreatorDialogOpen() is already false.
        releaseOpenWait();
        resolveOpened();
      };
      const failOpened = (error: Error): void => {
        if (openedSettled) return;
        openedSettled = true;
        releaseOpenWait();
        rejectOpened(error);
      };
      reviewOpenWaitRef.current = opened;
      cancelReviewOpenWaitRef.current = markOpened;

      void (async () => {
        let targetPresentationRef: FactSetPresentationRef | null = null;
        const reviewLog = (message: string): void => {
          if (isSessionCurrent()) {
            log(message);
          }
        };
        if (!isSessionCurrent()) {
          markOpened();
          return;
        }
        if (shouldUseFactSetBridge()) {
          try {
            if (!isSessionCurrent()) {
              markOpened();
              return;
            }
            targetPresentationRef = await getActivePresentationRefViaIntents(reviewLog);
            if (!isSessionCurrent()) {
              markOpened();
              return;
            }
          } catch (error) {
            if (!isSessionCurrent()) {
              markOpened();
              return;
            }
            reviewLog(
              `Could not capture target presentation before deck review: ${
                error instanceof Error ? error.message : String(error)
              }`
            );
          }
        }
        if (!isSessionCurrent()) {
          markOpened();
          return;
        }
        try {
          if (!isSessionCurrent()) {
            markOpened();
            return;
          }
          const result = await openDeckCreatorDialog({
            tasks: effectiveUserTasks,
            userEmail: authState.user?.email || "",
            userCompanyId: authState.user?.companyId || "",
            initialPlan: stripDeckPlanTransientContext(
              resolveDeckCreatorOpenPlan(dialogPlan) ?? dialogPlan
            ),
            onOpened: () => {
              if (isSessionCurrent()) {
                markOpened();
              }
            },
            beforeConfirm: async () => {
              if (!isSessionCurrent()) return;
              await waitForActiveReviewOperationIdle();
              if (!isSessionCurrent()) return;
            },
            onPlanPatch: (patchedPlan) => {
              if (!isSessionCurrent()) return;
              const safePatchedPlan = stripDeckPlanTransientContext(patchedPlan);
              const reboundPlan = reattachDeckPlanTransientContext(
                safePatchedPlan,
                activeReviewContextRef.current
              );
              pendingDeckPlanRef.current = reboundPlan;
              setPendingDeckPlan(reboundPlan);
            },
          });
          if (!isSessionCurrent()) {
            markOpened();
            return;
          }
          markOpened();
          if (result.cancelled) {
            releaseActiveReviewBinding();
            pendingDeckPlanRef.current = null;
            clearPlannedBrandStyle();
            setPendingDeckPlan(null);
            setPendingDeckConfig(null);
            setPendingDeckPresentationRef(null);
            log("Deck plan review cancelled");
            return;
          }
          if (result.error) {
            releaseActiveReviewBinding();
            pendingDeckPlanRef.current = null;
            clearPlannedBrandStyle();
            setPendingDeckPlan(null);
            setPendingDeckConfig(null);
            setPendingDeckPresentationRef(null);
            log(`Deck creator error: ${result.error}`);
            return;
          }
          if (!result.config || !result.plan) {
            releaseActiveReviewBinding();
            pendingDeckPlanRef.current = null;
            clearPlannedBrandStyle();
            setPendingDeckPlan(null);
            return;
          }
          const activeContext = activeReviewContextRef.current;
          const latestPrivatePlan = pendingDeckPlanRef.current;
          const latestBelongsToOperation =
            activeContext &&
            latestPrivatePlan?.reviewOperationId === activeContext.reviewOperationId;
          const approvedSafePlan = stripDeckPlanTransientContext(
            latestBelongsToOperation ? latestPrivatePlan : result.plan
          );
          const approvedPlan = freezeApprovedDeckPlanContext(approvedSafePlan, activeContext);
          releaseActiveReviewBinding();
          const attached = taskContextRef.current;
          const confirmedPlan = attached
            ? {
                ...approvedPlan,
                groundingContextForSlides: attached.content,
                groundingMessage: attached.name,
                groundingMode: "run_output" as const,
                groundingCitations: attached.citations,
              }
            : approvedPlan;
          setPendingDeckConfig({
            ...result.config,
            brandId: plannedBrandStyleRef.current,
          });
          setPendingDeckPlan(confirmedPlan);
          pendingDeckPlanRef.current = confirmedPlan;
          setPendingDeckPresentationRef(targetPresentationRef);
          setCurrentView("chat");
          setAiMode("create");
          log(
            `Using plan from dialog: ${result.config.topic} (${confirmedPlan.slides.length} slides)`
          );
        } catch (err) {
          if (!isSessionCurrent()) {
            markOpened();
            return;
          }
          const error = err instanceof Error ? err : new Error(String(err));
          failOpened(error);
          releaseActiveReviewBinding();
          setPendingDeckPlan(null);
          pendingDeckPlanRef.current = null;
          clearPlannedBrandStyle();
          setPendingDeckConfig(null);
          setPendingDeckPresentationRef(null);
          log(`Failed to open deck creator dialog: ${error.message}`);
        } finally {
          if (isSessionCurrent()) {
            releaseOpenWait();
          }
          markOpened();
        }
      })();

      return opened;
    },
    [
      accountScopeKey,
      authState.user?.email,
      authState.user?.companyId,
      log,
      releaseActiveReviewBinding,
      effectiveUserTasks,
      waitForActiveReviewOperationIdle,
    ]
  );

  const handleUpdateDeckPlan = useCallback((plan: DeckPlan): boolean => {
    if (!isDeckCreatorReviewSessionOpen()) {
      return false;
    }
    const incomingContext = getDeckPlanTransientContext(plan);
    const currentContext = activeReviewContextRef.current;
    const currentOperationId =
      currentContext?.reviewOperationId ?? activeReviewOperationIdRef.current;
    const currentGeneration =
      currentContext?.reviewOperationGeneration ?? activeReviewOperationGenerationRef.current;
    if (
      incomingContext &&
      currentOperationId &&
      (incomingContext.reviewOperationId !== currentOperationId ||
        incomingContext.reviewOperationGeneration !== currentGeneration)
    ) {
      return false;
    }
    const nextContext =
      incomingContext && currentContext
        ? Object.freeze({
            ...incomingContext,
            ...(incomingContext.planningEvidence
              ? undefined
              : currentContext.planningEvidence
                ? { planningEvidence: currentContext.planningEvidence }
                : undefined),
          })
        : (incomingContext ?? currentContext);
    activeReviewContextRef.current = nextContext ?? null;
    activeReviewOperationIdRef.current =
      nextContext?.reviewOperationId ?? currentOperationId ?? null;
    activeReviewOperationGenerationRef.current =
      nextContext?.reviewOperationGeneration ?? currentGeneration;
    const privatePlan = reattachDeckPlanTransientContext(
      stripDeckPlanTransientContext(plan),
      nextContext
    );
    pendingDeckPlanRef.current = privatePlan;
    setPendingDeckPlan(privatePlan);
    pushPlanToDeckCreatorDialog(stripDeckPlanTransientContext(privatePlan));
    return true;
  }, []);

  const handleReviewSkillContextResolved = useCallback(
    (
      reviewOperationId: string,
      reviewOperationGeneration: number,
      reviewSkillBindingState: Exclude<ReviewSkillBindingState, "unbound">,
      skillRunContext: SkillRunContext | undefined
    ): boolean => {
      if (
        (reviewSkillBindingState === "bound" && !skillRunContext) ||
        (reviewSkillBindingState === "cleared" && skillRunContext)
      ) {
        return false;
      }
      const currentPlan = pendingDeckPlanRef.current;
      const reviewIsActive =
        Boolean(currentPlan) &&
        (isDeckCreatorReviewSessionOpen() || Boolean(reviewOpenWaitRef.current));
      if (!currentPlan || !reviewIsActive) return false;

      const currentContext = activeReviewContextRef.current;
      const planContext = getDeckPlanTransientContext(currentPlan);
      const currentOperationId =
        currentContext?.reviewOperationId ??
        activeReviewOperationIdRef.current ??
        planContext?.reviewOperationId;
      const currentGeneration =
        currentContext?.reviewOperationGeneration ??
        planContext?.reviewOperationGeneration ??
        activeReviewOperationGenerationRef.current;
      if (
        (currentOperationId && currentOperationId !== reviewOperationId) ||
        currentGeneration !== reviewOperationGeneration
      ) {
        return false;
      }
      const planningEvidence =
        currentContext?.reviewOperationId === reviewOperationId
          ? currentContext.planningEvidence
          : planContext?.reviewOperationId === reviewOperationId
            ? planContext.planningEvidence
            : undefined;
      const nextContext: DeckPlanTransientContext = Object.freeze({
        reviewOperationId,
        reviewOperationGeneration,
        reviewSkillBindingState,
        ...(skillRunContext ? { skillRunContext } : undefined),
        ...(planningEvidence ? { planningEvidence } : undefined),
      });
      activeReviewContextRef.current = nextContext;
      activeReviewOperationIdRef.current = reviewOperationId;
      activeReviewOperationGenerationRef.current = reviewOperationGeneration;
      const reboundPlan = reattachDeckPlanTransientContext(
        stripDeckPlanTransientContext(currentPlan),
        nextContext
      );
      pendingDeckPlanRef.current = reboundPlan;
      setPendingDeckPlan(reboundPlan);
      return true;
    },
    []
  );

  const handleReviewPlanningEvidence = useCallback(
    (
      reviewOperationId: string,
      reviewOperationGeneration: number,
      planningEvidence: PlanningEvidencePack
    ) => {
      const currentContext = activeReviewContextRef.current;
      if (
        !currentContext ||
        currentContext.reviewOperationId !== reviewOperationId ||
        currentContext.reviewOperationGeneration !== reviewOperationGeneration ||
        planningEvidence.reviewOperationId !== reviewOperationId
      ) {
        return;
      }
      const nextContext: DeckPlanTransientContext = Object.freeze({
        ...currentContext,
        planningEvidence,
      });
      activeReviewContextRef.current = nextContext;
      const currentPlan = pendingDeckPlanRef.current;
      if (!currentPlan) return;
      const reboundPlan = reattachDeckPlanTransientContext(
        stripDeckPlanTransientContext(currentPlan),
        nextContext
      );
      pendingDeckPlanRef.current = reboundPlan;
      setPendingDeckPlan(reboundPlan);
    },
    []
  );

  const getPrivatePendingDeckPlan = useCallback((): DeckPlan | undefined => {
    if (operationOwnerRef.current !== accountScopeKeyRef.current) {
      return undefined;
    }
    const plan = getRememberedDeckCreatorPlan() ?? pendingDeckPlanRef.current ?? undefined;
    if (!plan) return undefined;
    return reattachDeckPlanTransientContext(
      stripDeckPlanTransientContext(plan),
      activeReviewContextRef.current
    );
  }, []);

  const [previousOnboardingUser, setPreviousOnboardingUser] = React.useState(storageUserId);
  if (previousOnboardingUser !== storageUserId) {
    setPreviousOnboardingUser(storageUserId);
    setOnboardingDismissed(false);
  }

  const handleCompleteOnboarding = useCallback(() => {
    if (storageUserId) {
      markOnboardingCompleted("powerpoint", storageUserId);
    }
    setOnboardingDismissed(true);
  }, [storageUserId]);

  const matchingSkillBanner = useMatchingSkillBanner({
    isLoading: Boolean(activeTab?.isLoading),
  });
  const hasPendingSkillDecisionRef = useRef(false);
  hasPendingSkillDecisionRef.current = matchingSkillBanner.state !== null;

  const {
    result,
    setResult,
    handleQuery,
    cancelQuery,
    cancelAllQueries,
    handleReplaceSkillRun,
    setPendingFixQuery,
    cameFromReview,
    setCameFromReview,
  } = useQueryHandler({
    activeTab,
    updateActiveTab,
    setTabsState,
    syncTabsWithPresentation,
    taskContext: effectiveTaskContext,
    aiMode,
    log,
    brandId: brandStyle,
    plannerMode,
    companyId: activeBrand?.companyId || authState.user?.companyId || "",
    accountScopeKey,
    pendingDeckPlan: privateOperationStateIsCurrent ? pendingDeckPlan : null,
    onPresentDeckPlan: handlePresentDeckPlan,
    onUpdateDeckPlan: handleUpdateDeckPlan,
    getPendingDeckPlan: getPrivatePendingDeckPlan,
    isReviewSessionOpen: isDeckCreatorReviewSessionOpen,
    onReviewPlanQueryStarted: capturePlannedBrandStyle,
    onReviewPlanningEvidence: handleReviewPlanningEvidence,
    onReviewOperationActivity: registerReviewOperationActivity,
    onReviewSkillContextResolved: handleReviewSkillContextResolved,
    onSkillSubmission: matchingSkillBanner.noteSubmission,
    onSkillDecision: matchingSkillBanner.setDecision,
    onDismissSkillDecision: matchingSkillBanner.dismiss,
  });
  const handleChatQuery = useCallback(
    async (
      query: Parameters<typeof handleQuery>[0],
      dataSources?: Parameters<typeof handleQuery>[1],
      userFileIds?: Parameters<typeof handleQuery>[2],
      skillSubmission?: Parameters<typeof handleQuery>[3]
    ): Promise<boolean> => {
      if (!activeTab) return false;
      try {
        const pendingReviewPlan =
          privateOperationStateIsCurrent && isDeckCreatorReviewSessionOpen()
            ? getPrivatePendingDeckPlan()
            : undefined;
        if (pendingReviewPlan) {
          getDeckPlanTransientContext(pendingReviewPlan);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        log(`Chat setup failed: ${message}`);
        setResult({
          success: false,
          message:
            "The pending Review state could not be submitted. Please reopen Review and try again.",
        });
        return false;
      }
      try {
        // Resolves at accepted handoff, not when the chat pipeline finishes.
        const submission = await handleQuery(query, dataSources, userFileIds, skillSubmission);
        return submission !== false;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        log(`Chat setup failed: ${message}`);
        setResult({
          success: false,
          message: "The request could not be submitted. Please try again.",
        });
        return false;
      }
    },
    [
      activeTab,
      getPrivatePendingDeckPlan,
      handleQuery,
      log,
      privateOperationStateIsCurrent,
      setResult,
    ]
  );
  const handleChatUserFileIdsChange = useCallback(
    (fileIds: string[]) => {
      if (!activeTab) return;
      // Stored on the session, so the files stay with the conversation that
      // attached them: another tab, deck or account reads its own list, and a
      // reload brings this one back.
      updateActiveTab({
        session: {
          ...activeTab.session,
          selectedUserFileIds: fileIds,
          updatedAt: Date.now(),
        },
      });
    },
    [activeTab, updateActiveTab]
  );
  const handleDeckOperationSettled = useCallback(
    (plan: DeckPlan) => {
      const context = getDeckPlanTransientContext(plan);
      if (!context) return;
      reviewOperationActivitiesRef.current.get(context.reviewOperationId)?.supersede();
      reviewOperationActivitiesRef.current.delete(context.reviewOperationId);
      const activeReviewOperationId =
        activeReviewContextRef.current?.reviewOperationId ?? activeReviewOperationIdRef.current;
      if (activeReviewOperationId === context.reviewOperationId) {
        releaseActiveReviewBinding();
      }
      if (pendingDeckPlanRef.current?.reviewOperationId === context.reviewOperationId) {
        pendingDeckPlanRef.current = null;
        setPendingDeckPlan(null);
        setPendingDeckConfig(null);
        setPendingDeckPresentationRef(null);
      }
    },
    [releaseActiveReviewBinding]
  );
  const {
    deckCreationProgress,
    ongoingDeckCreationProgress,
    isCreatingDeck,
    handleCancelCreation,
    cancelAllDeckOperations,
    createDeck,
  } = useDeckCreation({
    aiMode,
    setAiMode,
    syncTabsWithPresentation,
    log,
    setTabsState,
    accountScopeKey,
    companyId: authState.user?.companyId,
    onDeckOperationSettled: handleDeckOperationSettled,
  });
  isCreatingDeckRef.current = isCreatingDeck;
  useEffect(() => {
    const controller = createClientUpdateController({
      currentBuildVersion: getClientBuildVersion(),
      fetchVersion: fetchCurrentVersion,
      isBusy: () =>
        isPowerPointClientUpdateBusy({
          hasLoadingTab: tabsStateRef.current.tabs.some((tab) => Boolean(tab.isLoading)),
          hasInFlightHostWork: hasAnyInFlightPowerPointWork(),
          isCreatingDeck: isCreatingDeckRef.current,
          hasPendingSkillDecision: hasPendingSkillDecisionRef.current,
          hasPendingReview: Boolean(
            pendingDeckPlanRef.current || isDeckCreatorReviewSessionOpen()
          ),
          hasReviewActivity:
            reviewOperationActivitiesRef.current.size > 0 ||
            Boolean(deckReviewRef.current?.isReviewing),
        }),
      beforeReload: () => saveTabsState(tabsStateRef.current),
      reload: (version) => {
        const url = new URL(window.location.href);
        url.searchParams.set("_build", version);
        window.location.replace(url.toString());
      },
      onUpdatePending: setClientUpdatePending,
      windowTarget: window,
      documentTarget: document,
    });
    clientUpdateControllerRef.current = controller;
    const unsubscribeUpgradeRequired = subscribeToUpgradeRequired((version) => {
      void controller.handleUpgradeRequired(version);
    });
    controller.start();

    return () => {
      unsubscribeUpgradeRequired();
      controller.stop();
      if (clientUpdateControllerRef.current === controller) {
        clientUpdateControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    clientUpdateControllerRef.current?.notifyStateChanged();
  }, [tabsState, isCreatingDeck, matchingSkillBanner.state, pendingDeckPlan]);

  useEffect(() => {
    if (!clientUpdatePending) return undefined;
    const handle = window.setInterval(() => {
      clientUpdateControllerRef.current?.notifyStateChanged();
    }, PENDING_UPDATE_IDLE_RECHECK_MS);
    return () => window.clearInterval(handle);
  }, [clientUpdatePending]);

  const activeRuntimeOwnerRef = useRef(accountScopeKey);
  useEffect(() => {
    const ownerChanged = activeRuntimeOwnerRef.current !== accountScopeKey;
    activeRuntimeOwnerRef.current = accountScopeKey;
    if (authState.isAuthenticated && !ownerChanged) return;
    cancelAllQueries();
    cancelAllDeckOperations();
    taskContextOwnerRef.current = accountScopeKey;
    setTaskContext(null);
    pdfStateOwnerRef.current = accountScopeKey;
    setPdfExtractedImages([]);
    setPdfSelectedImages(new Set());
    userTasksOwnerRef.current = accountScopeKey;
    setUserTasks([]);
    setTasksError(null);
    setTasksLoading(false);
    setPlannerMode(false);
    clearPlannedBrandStyle();
    setCurrentView("chat");
    setSkillsContextVersion((version) => version + 1);
  }, [accountScopeKey, authState.isAuthenticated, cancelAllDeckOperations, cancelAllQueries]);
  // Stable key derived from the SET of target presentation IDs across
  // all ongoing decks. `ongoingDeckCreationProgress` itself gets a new
  // array reference on every progress tick (the underlying memo is
  // keyed on `deckProgressByOperationId`, which is replaced wholesale
  // by each progress callback), so depending on it directly would
  // re-fire any consumer effect on every tick. The sync-mode effect
  // below makes an async `getActivePresentationRefViaIntents` IPC
  // call, so a per-tick re-fire would issue hundreds of IPC requests
  // per deck run. Keying on the sorted+joined target-id string means
  // we only re-sync when the set of target presentations actually
  // changes (a deck started or finished).
  const ongoingDeckTargetIdsKey = useMemo(
    () =>
      ongoingDeckCreationProgress
        .map(({ progress }) => progress.targetPresentationId?.trim() || "")
        .filter((id) => id.length > 0)
        .sort()
        .join("|"),
    [ongoingDeckCreationProgress]
  );
  useEffect(() => {
    deckCreationTargetPresentationIdsRef.current = ongoingDeckTargetIdsKey
      ? ongoingDeckTargetIdsKey.split("|")
      : [];
  }, [ongoingDeckTargetIdsKey]);
  useEffect(() => {
    syncDeckCreationModeWithActivePresentation();
  }, [
    isCreatingDeck,
    ongoingDeckTargetIdsKey,
    pendingDeckPresentationRef?.openPresentationId,
    syncDeckCreationModeWithActivePresentation,
  ]);

  const deckReview = useDeckReview({
    log,
    isAuthenticated: authState.isAuthenticated,
    accountScopeKey,
  });
  deckReviewRef.current = deckReview;
  useEffect(() => {
    clientUpdateControllerRef.current?.notifyStateChanged();
  }, [deckReview.isReviewing]);

  const prevAiModeRef = useRef(aiMode);
  useEffect(() => {
    if (prevAiModeRef.current === "review" && aiMode !== "review") {
      deckReviewRef.current?.clearReviewAndGoBack();
    }
    prevAiModeRef.current = aiMode;
  }, [aiMode]);

  const tasksRequestKey = `${accountScopeKey}:${authState.isAuthenticated}:${authState.user?.email ?? ""}`;
  const [previousTasksRequestKey, setPreviousTasksRequestKey] = useState<string | null>(null);
  if (previousTasksRequestKey !== tasksRequestKey) {
    setPreviousTasksRequestKey(tasksRequestKey);
    setUserTasks([]);
    setTasksError(null);
    setTasksLoading(Boolean(authState.isAuthenticated && accountScopeKey && authState.user?.email));
  }

  // Fetch user tasks when authenticated
  useEffect(() => {
    let cancelled = false;
    const email = authState.user?.email;
    userTasksOwnerRef.current = accountScopeKey;
    // Clear again after cleanup: an old response may have queued updates since the render reset.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUserTasks([]);
    setTasksError(null);
    setTasksLoading(Boolean(authState.isAuthenticated && accountScopeKey && email));
    if (!authState.isAuthenticated || !accountScopeKey || !email) {
      return () => {
        cancelled = true;
      };
    }

    log(`Fetching tasks for user: ${email}`);
    void getUserTasks(email)
      .then((result) => {
        if (cancelled) return;
        setUserTasks(result.tasks);
        log(`Loaded ${result.tasks.length} tasks for user`);
        if (result.error) {
          setTasksError(result.error);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load tasks:", err);
        log(`Tasks fetch error: ${err}`);
        setTasksError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setTasksLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountScopeKey, authState.isAuthenticated, authState.user?.email]);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background font-sans">
        <Spinner size="sm" />
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!authState.isAuthenticated) {
    return (
      <Login onLogin={handleLogin} enableStandaloneSsoLogin={shouldAttemptFactSetAutoLogin()} />
    );
  }

  if (shouldShowOnboarding) {
    return <Onboarding onComplete={handleCompleteOnboarding} />;
  }

  return (
    <SidebarProvider collapsed={sideNavCollapsed} onCollapsedChange={setSideNavCollapsed}>
      <SidebarInset className="flex h-screen max-h-screen min-h-0 flex-col overflow-hidden bg-background font-sans">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {currentView === "pdf" && (
            <PdfUploader
              onLog={logPdfActivity}
              extractedImages={effectivePdfExtractedImages}
              setExtractedImages={setScopedPdfExtractedImages}
              selectedImages={effectivePdfSelectedImages}
              setSelectedImages={setScopedPdfSelectedImages}
              onOpenSidebar={() => setSideNavCollapsed(false)}
            />
          )}

          {currentView === "home" && (
            <TasksHome
              tasks={effectiveUserTasks}
              loading={effectiveTasksLoading}
              error={effectiveTasksError}
              userEmail={authState.user?.email || ""}
              onUseForDeck={(content, name, citations) => {
                updateTaskContext({ content, name, citations });
                setCurrentView("chat");
                log(`Loaded task context: ${name} (${content.length} chars)`);
              }}
            />
          )}

          {(currentView === "chat" || currentView === "skills") && (
            <>
              {aiMode === "create" && (
                <DeckCreator
                  onLog={log}
                  onCancel={() => handleCancelCreation()}
                  externalProgress={deckCreationProgress}
                  isCreating={isCreatingDeck}
                  onCreateDeck={createDeck}
                  initialConfig={
                    privateOperationStateIsCurrent ? (pendingDeckConfig ?? undefined) : undefined
                  }
                  initialPlan={
                    privateOperationStateIsCurrent ? (pendingDeckPlan ?? undefined) : undefined
                  }
                  initialPresentationRef={
                    privateOperationStateIsCurrent ? pendingDeckPresentationRef : null
                  }
                  onClearInitialConfig={() => {
                    setPendingDeckConfig(null);
                    setPendingDeckPlan(null);
                    pendingDeckPlanRef.current = null;
                  }}
                />
              )}

              {aiMode === "review" && (
                <DeckReview
                  onFixIssue={(query) => {
                    setCameFromReview(true);
                    setAiMode("chat");
                    setTimeout(() => {
                      setPendingFixQuery(query);
                    }, 200);
                  }}
                  onOpenSidebar={() => setSideNavCollapsed(false)}
                  reviewResults={deckReview.reviewResults}
                  isReviewing={deckReview.isReviewing}
                  reviewProgress={deckReview.reviewProgress}
                  slideCount={deckReview.slideCount}
                  selectedSlides={deckReview.selectedSlides}
                  setSelectedSlides={deckReview.setSelectedSlides}
                  openSlideSelector={deckReview.openSlideSelector}
                  startReview={deckReview.startReview}
                  clearResults={deckReview.clearResults}
                  goToSlide={deckReview.goToSlide}
                />
              )}

              {aiMode === "chat" && (
                <>
                  {ongoingDeckCreationProgress.length > 0 && (
                    <BackgroundDeckCreationBanner
                      items={ongoingDeckCreationProgress.map(({ operationId, progress }) => {
                        const targetPresentationId = progress.targetPresentationId?.trim() || "";
                        const currentSlideId = progress.currentSlideId?.trim() || "";
                        const onGoToSlide =
                          !targetPresentationId && !currentSlideId
                            ? undefined
                            : () => {
                                void (async () => {
                                  try {
                                    if (targetPresentationId && shouldUseFactSetBridge()) {
                                      await activatePresentationViaIntents({
                                        presentationRef: {
                                          openPresentationId: targetPresentationId,
                                        },
                                        log,
                                      });
                                    }
                                    if (currentSlideId) {
                                      await goToSlideById(currentSlideId);
                                    }
                                  } catch (err) {
                                    log(
                                      `Could not switch to in-progress presentation: ${
                                        err instanceof Error ? err.message : String(err)
                                      }`
                                    );
                                  }
                                })();
                              };
                        return {
                          operationId,
                          progress,
                          onCancel: () => handleCancelCreation(operationId),
                          onGoToSlide,
                        };
                      })}
                    />
                  )}
                  {clientUpdatePending && (
                    <div className="px-3 pb-1" role="status">
                      <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-900">
                        Finster will update as soon as the current PowerPoint work is safe to pause.
                      </div>
                    </div>
                  )}
                  <ChatView
                    key={accountScopeKey ?? "anonymous"}
                    cameFromReview={cameFromReview}
                    deckReviewResultsAvailable={deckReview.reviewResults.length > 0}
                    setCameFromReview={setCameFromReview}
                    setAiMode={setAiMode}
                    taskContext={effectiveTaskContext}
                    setTaskContext={updateTaskContext}
                    activeTab={activeTab}
                    result={result}
                    handleQuery={handleChatQuery}
                    onCancelQuery={cancelQuery}
                    matchingSkillBanner={matchingSkillBanner}
                    onReplaceSkillRun={handleReplaceSkillRun}
                    isDeckEmpty={deckHasSlides === false}
                    pdfExtractedImages={effectivePdfExtractedImages}
                    log={log}
                    sortedTabs={sortedTabs}
                    onSelectTab={handleSelectTabFromNav}
                    onViewAllChats={() => handleSideNavAction("view-all-chats")}
                    brandStyle={brandStyle}
                    setBrandStyle={setBrandStyle}
                    plannerMode={plannerMode}
                    setPlannerMode={setPlannerMode}
                    featureFlags={authState.features?.featureFlags}
                    featureFlagsLoading={!authChecked}
                    userEmail={authState.user?.email}
                    userCompanyId={authState.user?.companyId}
                    appEnv={authState.features?.auth?.appEnv}
                    selectedUserFileIds={activeTab?.session.selectedUserFileIds}
                    onUserFileIdsChange={handleChatUserFileIdsChange}
                    uploadStateKey={JSON.stringify([
                      accountScopeKey,
                      tabsState.documentId,
                      activeTab?.session.id,
                    ])}
                    skillsStateKey={JSON.stringify([
                      accountScopeKey,
                      tabsState.documentId,
                      activeTab?.id,
                      skillsContextVersion,
                    ])}
                    skillsViewOpen={currentView === "skills"}
                    skillsComposerFocusKey={skillsComposerFocusKey}
                    manageSkillsUrl={manageSkillsUrl}
                    onOpenSkills={handleOpenSkills}
                    onCloseSkills={handleCloseSkills}
                    onTrySkillInChat={handleCloseSkills}
                  />
                </>
              )}
            </>
          )}

          {currentView === "allChats" && (
            <AllChatsView
              sortedTabs={sortedTabs}
              activeTabId={tabsState.activeTabId}
              onSelectTab={handleSelectTabFromNav}
              onClose={() => {
                setCurrentView("chat");
                setAiMode("chat");
              }}
              searchFocusKey={allChatsSearchFocusKey}
            />
          )}

          {currentView === "citations" && (
            <SlideCitationsPanel
              slideIndex={activeTab ? activeTab.session.slideIndex : 0}
              slideNumber={activeTab?.session.slideNumber}
              slideId={activeTab?.session.slideId}
              onClose={() => {
                setCurrentView("chat");
                setAiMode("chat");
              }}
            />
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default App;
